import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { notifySuccess, notifyError } from '../../../services/notifications';
import { coursesApiSlice } from '../../courses/coursesApi';
import { courseApiSlice } from '../courseApi';
import { useFeatures } from '../../settings/hooks';

export function useCourseActions() {
  const dispatch = useDispatch();
  const features = useFeatures();

  const [createCourseTrigger] = courseApiSlice.useCreateCourseMutation();
  const [updateCourseTrigger] = courseApiSlice.useUpdateCourseMutation();
  const [deleteCourseTrigger] = courseApiSlice.useDeleteCourseMutation();
  const [toggleCourseDraftTrigger] = courseApiSlice.useToggleCourseDraftMutation();
  const [duplicateCourseTrigger] = courseApiSlice.useDuplicateCourseMutation();
  const [changeAccessToCourseTrigger] = courseApiSlice.useChangeAccessToCourseMutation();

  const invalidateCourses = useCallback(() => {
    try { dispatch(coursesApiSlice.util.invalidateTags(['Courses'])); } catch (_) {}
  }, [dispatch]);

  const draftCourses = features?.draftCourses;

  const actions = useMemo(() => ({
    createCourse: async ({ name, coverImage }) => {
      try {
        const data = await createCourseTrigger({ name, coverImage }).unwrap();
        notifySuccess('course.createdSuccess');
        if (draftCourses && data?.id) {
          try {
            await toggleCourseDraftTrigger({ id: data.id }).unwrap();
            notifySuccess('course.draftUpdated');
          } catch (e) {
            notifyError(e?.data?.response?.errMsg || 'Failed to toggle draft');
          }
        }
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.createFailed');
        throw e;
      }
    },

    updateCourse: async ({ courseData }) => {
      try {
        const data = await updateCourseTrigger({ courseData }).unwrap();
        notifySuccess('course.updatedSuccess');
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.updateFailed');
        throw e;
      }
    },

    deleteCourse: async (id) => {
      try {
        const data = await deleteCourseTrigger(id).unwrap();
        notifySuccess('course.deletedSuccess');
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.deleteFailed');
        throw e;
      }
    },

    toggleCourseDraft: async ({ id }) => {
      try {
        const data = await toggleCourseDraftTrigger({ id }).unwrap();
        notifySuccess('course.draftUpdated');
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.draftUpdateFailed');
        throw e;
      }
    },

    duplicateCourse: async (id) => {
      try {
        const data = await duplicateCourseTrigger(id).unwrap();
        notifySuccess('course.duplicatedSuccess');
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.duplicateFailed');
        throw e;
      }
    },

    changeAccessToCourse: async ({ id, expires, userId, mode }) => {
      try {
        const data = await changeAccessToCourseTrigger({ id, expires, userId, mode }).unwrap();
        notifySuccess('course.accessUpdated');
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'course.accessUpdateFailed');
        throw e;
      }
    },

    /**
     * Update quiz settings for a specific page
     * @param {Object} params
     * @param {Object} params.course - Current course data
     * @param {number} params.pageId - Quiz page ID
     * @param {Object} params.settings - Quiz settings to update
     */
    updateQuizSettings: async ({ course, pageId, settings }) => {
      try {
        const updatedSettings = {
          ...course?.settings,
          quiz: {
            ...course?.settings?.quiz,
            enabled: true,
            pages: {
              ...course?.settings?.quiz?.pages,
              [pageId]: {
                ...course?.settings?.quiz?.pages?.[pageId],
                ...settings,
              },
            },
          },
        };

        const data = await updateCourseTrigger({
          courseData: {
            id: course.id,
            categories: course.categories,
            settings: updatedSettings,
          },
        }).unwrap();
        notifySuccess('quiz.settingsUpdated');
        invalidateCourses();
        return data;
      } catch (e) {
        notifyError(e?.data?.response?.errMsg || 'quiz.settingsUpdateFailed');
        throw e;
      }
    },
  }), [
    createCourseTrigger,
    updateCourseTrigger,
    deleteCourseTrigger,
    toggleCourseDraftTrigger,
    duplicateCourseTrigger,
    changeAccessToCourseTrigger,
    invalidateCourses,
    draftCourses,
  ]);

  return actions;
}

// Action-specific hooks, mirroring Auth pattern
export const useCreateCourse = () => useCourseActions().createCourse;
export const useUpdateCourse = () => useCourseActions().updateCourse;
export const useDeleteCourse = () => useCourseActions().deleteCourse;
export const useToggleCourseDraft = () => useCourseActions().toggleCourseDraft;
export const useDuplicateCourse = () => useCourseActions().duplicateCourse;
export const useChangeAccessToCourse = () => useCourseActions().changeAccessToCourse;
export const useUpdateQuizSettings = () => useCourseActions().updateQuizSettings;
